/**
 * Canonical triage palette — the client-held reference the history and
 * estimated-wait responses lean on. Those payloads carry only a `triageCode`
 * (see TriageHistorySeries / TriageWaitEstimateDto), deliberately not repeating
 * the labels/colour on every point; the client maps the code through this
 * palette. It mirrors the backend `triage_levels` seed (1 = Rosso / most
 * urgent … 5 = Blu), so a self-contained detail screen renders correctly even
 * when reached by direct URL with no overview response in cache.
 *
 * Colour is never the only channel anywhere it's used — every consumer pairs the
 * hue with the Italian label (WCAG 2.1 AA, see DECISIONS.md).
 *
 * The rendered hue is single-sourced through the quarantined `--t-*` CSS
 * variables (see `index.css`), so triage colours theme correctly in light and
 * dark from one place — consumers use {@link TriageMeta.colorVar}, not a
 * hardcoded hex. `colorHex` is retained only as a theme-agnostic fallback for
 * environments without the design tokens (e.g. unit tests / SSR snapshots).
 */

export interface TriageMeta {
  /** Triage code [1, 5] (1 = Rosso / most urgent). */
  code: number;
  /** Italian label (e.g. "Rosso"). */
  labelIt: string;
  /** German label (e.g. "Rot"). */
  labelDe: string;
  /**
   * Themed colour as a CSS `var(--t-*)` reference — the value rendered swatches
   * and chart strokes use, so triage hues re-tune per theme from `index.css`.
   * Falls back to {@link colorHex} for unknown codes.
   */
  colorVar: string;
  /** Theme-agnostic reference hex (fallback; matches the light-theme `--t-*`). */
  colorHex: string;
  /** Severity rank (1 = most urgent). */
  severityRank: number;
  /**
   * Official ASDAA urgency-level name for this colour (e.g. "Emergenza"),
   * as printed on the triage reference sheet at the pronto soccorso.
   */
  levelNameIt: string;
  /**
   * Official ASDAA description of what the level means, verbatim from the
   * triage reference sheet — shown in the user legend.
   */
  descriptionIt: string;
}

const TRIAGE: Record<number, TriageMeta> = {
  1: {
    code: 1,
    labelIt: "Rosso",
    labelDe: "Rot",
    colorVar: "var(--t-red)",
    colorHex: "#e5484d",
    severityRank: 1,
    levelNameIt: "Emergenza",
    descriptionIt: "Interruzione o compromissione di una o più funzionalità vitali.",
  },
  2: {
    code: 2,
    labelIt: "Arancione",
    labelDe: "Orange",
    colorVar: "var(--t-orange)",
    colorHex: "#f76808",
    severityRank: 2,
    levelNameIt: "Urgenza",
    descriptionIt:
      "Rischio di compromissione delle funzionalità vitali. Condizione con rischio evolutivo o dolore severo.",
  },
  3: {
    code: 3,
    labelIt: "Giallo",
    labelDe: "Gelb",
    colorVar: "var(--t-yellow)",
    colorHex: "#f5a623",
    severityRank: 3,
    levelNameIt: "Urgenza differibile",
    descriptionIt:
      "Condizione stabile senza rischio evolutivo con sofferenza e ricaduta sullo stato generale che solitamente richiede prestazioni complesse.",
  },
  4: {
    code: 4,
    labelIt: "Verde",
    labelDe: "Grün",
    colorVar: "var(--t-green)",
    colorHex: "#30a46c",
    severityRank: 4,
    levelNameIt: "Urgenza minore",
    // A non-breaking space (\u00A0) ties the last two words so the long trailing
    // compound never strands alone on its own line — a widow `text-wrap: pretty`
    // declines to rebalance because the word isn't "short" (#112). Text unchanged.
    descriptionIt:
      "Condizione stabile senza rischio evolutivo che solitamente richiede prestazioni diagnostico-terapeutiche semplici\u00A0mono-specialistiche.",
  },
  5: {
    code: 5,
    labelIt: "Blu",
    labelDe: "Blau",
    colorVar: "var(--t-blue)",
    colorHex: "#3b82f6",
    severityRank: 5,
    levelNameIt: "Non urgenza",
    descriptionIt: "Problema non urgente di minima rilevanza clinica.",
  },
};

/** The five canonical triage levels, ordered most-urgent first — for the legend. */
export const TRIAGE_LEVELS: readonly TriageMeta[] = [1, 2, 3, 4, 5].map((c) => TRIAGE[c]!);

/** Fallback for an unknown/out-of-range triage code, so rendering never throws. */
const UNKNOWN_COLOR_VAR = "var(--muted)";
const UNKNOWN_COLOR_HEX = "#6b6470";

/** Look up the canonical metadata for a triage code; never throws. */
export function triageMeta(code: number): TriageMeta {
  return (
    TRIAGE[code] ?? {
      code,
      labelIt: `Codice ${code}`,
      labelDe: `Code ${code}`,
      colorVar: UNKNOWN_COLOR_VAR,
      colorHex: UNKNOWN_COLOR_HEX,
      severityRank: code,
      levelNameIt: `Codice ${code}`,
      descriptionIt: `Codice di triage ${code}.`,
    }
  );
}

/** Italian label for a triage code (e.g. 1 → "Rosso"). */
export function triageLabel(code: number): string {
  return triageMeta(code).labelIt;
}

/** Themed reference colour for a triage code, as a `var(--t-*)` reference. */
export function triageColor(code: number): string {
  return triageMeta(code).colorVar;
}
